// src/pages/seller/Seller2Net/components/ResultsModal.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { buildBuyerPayload } from "../../../../utils/payloadBuilder";
import { formatDateMDY } from "../../../../utils/format";
import { useModal } from "../../../../context/ModalContext";
import { useAuth, isGuestUser } from "../../../../context/AuthContext";
import {
  isOriginationRow,
  calcOriginationDollarValue,
  getOwnRowPercent,
} from "../../../../utils/originationFee";
import EmailModal from "../../popups/EmailModal";

export default function ResultsModal({
  step,
  setStep,
  onEditManual,
  formData,
  result,
  formatCurrency,
  cancelModal,
  handleSaveCalc,
  handleDownloadPrintPDF,
  getEffectiveFormData,
  cancelResultModal,
  showToaster,
  buyerformData,
  templateformData,
}) {
  const navigate = useNavigate();

  const { user } = useAuth();
  const { openModal, closeModal, closeAllModals } = useModal();

  const handleClose = () => {
    setStep(1);
    closeModal();
    closeAllModals();
  };

  const handleExit = (e) => {
    e.preventDefault();
    closeAllModals();
    setTimeout(() => navigate("/dashboard"), 0);
  };

  // Always read from the live form state first
  const liveData = buyerformData || formData || result || {};

  /* ── Safely resolve origination-factor % → dollar cost for display ──
     Covers the case where ManualSheetSection never mounted this session
     (e.g. reached this modal straight from the address/Calculate flow), so
     the row could still be sitting at its raw, unconverted percent here. */
  const getBuyerClosingCostValue = (item) => {
    if (!item) return "0.00";
    const unit = (item.unit || "").trim();
    if (isOriginationRow(item) && unit === "%") {
      return calcOriginationDollarValue(getOwnRowPercent(item), liveData.salesPrice);
    }
    return item.value || 0;
  };

  const getBuyerClosingCostUnit = (item) => {
    if (!item) return "$";
    const unit = (item.unit || "").trim();
    if (isOriginationRow(item) && unit === "%") return "$";
    return item.unit || "$";
  };

  return (
    <div className="popup-data-result model-popup-style">
      {step === 1 ? (
        <>
          <div className="model-body">
            <div className="heading-gp">
              <p className="heading-b">
                {liveData?.main_company_name} Calculator has calculated that on
                a <b className="txtclr">$ {liveData?.salesPrice}</b> sales price
                your Buyer will Total at Close <br />
                <b className="txtclr">
                  ${" "}
                  {formatCurrency(liveData?.totalClosingMeta?.totalInvestment)}
                </b>{" "}
                based upon the information you provided.
              </p>
            </div>

            <div className="tbl-top-details top-tbl-mrgn">
              <table>
                <tbody>
                  <tr>
                    <td className="txt-left heading-e txtclr">
                      <span className="clrbody">Prepared for:</span>{" "}
                      {liveData?.preparedFor || "New Client"},{" "}
                      {liveData?.addressMeta?.address || ""}
                    </td>
                    <td className="txt-right heading-e txtclr">
                      <span className="clrbody">Closing County Office:</span>{" "}
                      {liveData?.addressMeta?.county || "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="tbl-type-a popup-tbl-td-width">
              <h3>
                {liveData.loanInfoMeta?.buyersLoanType === "Cash" ? (
                  "Buyer's - Cash"
                ) : (
                  <>
                    Buyer's - {liveData.loanInfoMeta?.buyersLoanType} Loan at{" "}
                    {liveData.loanInfoMeta?.interestRate1} % Interest
                  </>
                )}
              </h3>
              <table cellPadding="0" cellSpacing="0">
                <tbody>
                  <tr>
                    <td>Est. Closing Date</td>
                    <td>
                      {formatDateMDY(liveData?.loanInfoMeta?.estimatedClosingDate)}
                    </td>
                  </tr>
                  <tr>
                    <td>Sell Price</td>
                    <td>$ {formatCurrency(liveData?.salesPrice)}</td>
                  </tr>
                  <tr>
                    <td>
                      1st Loan Amount 
                    </td>
                    <td>$ {formatCurrency(liveData.loanInfoMeta?.amount)}</td>
                  </tr>
                  {/* <tr>
                    <td>
                      2nd Loan Amount
                    </td>
                    <td>$ {formatCurrency(liveData.loanInfoMeta?.amount2)}</td>
                  </tr> */}
                  <tr>
                    <td>Down Payment</td>
                    <td>
                      ${" "}
                      {formatCurrency(liveData.loanInfoMeta?.buyerDownPayment)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="tbl-type-a popup-tbl-td-width">
              <h3>Closing Costs</h3>
              <div>
                <table cellPadding="0" cellSpacing="0">
                  <tbody>
                    <tr>
                      <td>Prepaid Costs</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData.totalClosingMeta?.totalPrepaids,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Total Costs</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData.totalClosingMeta?.totalClosingCosts,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Down Payment</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData.loanInfoMeta?.buyerDownPayment,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Estimated Tax Prorations</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData.paymentMeta?.estimatedTaxProrations,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>Bring to Closing</b>
                      </td>
                      <td>
                        <b>
                          ${" "}
                          {formatCurrency(
                            liveData.totalClosingMeta?.totalInvestment,
                          )}
                        </b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="tbl-type-a popup-tbl-td-width">
              <h3>Monthly Payment Info</h3>
              <div>
                <table cellPadding="0" cellSpacing="0">
                  <tbody>
                    <tr>
                      <td>Principal and Interest</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData.paymentMeta?.principalAndInterest,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Real Estate Taxes</td>
                      <td>
                        ${" "}
                        {formatCurrency(liveData.paymentMeta?.realEstateTaxes)}
                      </td>
                    </tr>
                    <tr>
                      <td>Home Owner Insurance</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData.paymentMeta?.homeownerInsurance,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>Monthly M.I.P</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData.paymentMeta?.rateMonthlyPMIAmount,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>P & I 2nd TD</td>
                      <td>
                        ${" "}
                        {formatCurrency(liveData.paymentMeta?.pAndI2ndTDAmount)}
                      </td>
                    </tr>
                    <tr>
                      <td>{liveData.paymentMeta?.monthlyExpense1}</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData.paymentMeta?.monthlyExpense1Amount,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>{liveData.paymentMeta?.monthlyExpense2}</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData.paymentMeta?.monthlyExpense2Amount,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>Total Payment (PITI)</b>
                      </td>
                      <td>
                        <b>
                          ${" "}
                          {formatCurrency(
                            liveData.totalClosingMeta?.totalMonthlyPayment,
                          )}
                        </b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="tbl-type-a popup-tbl-td-width">
              <h3>Fixed Closing Costs</h3>
              <div>
                <table cellPadding="0" cellSpacing="0">
                  <tbody>
                    <tr>
                      <td>Escrow or Settlement</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData?.titleSettlementMeta?.escrowSettlementAmount,
                        ) || 0.0}
                      </td>
                    </tr>
                    <tr>
                      <td>Owner Title Policy</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData?.titleSettlementMeta?.ownersTitleAmount,
                        ) || 0.0}
                      </td>
                    </tr>
                    <tr>
                      <td>Lenders Title Policy</td>
                      <td>
                        ${" "}
                        {formatCurrency(
                          liveData?.titleSettlementMeta?.lendersTitleAmount,
                        ) || 0.0}
                      </td>
                    </tr>

                    {liveData?.buyerClosingCostsMeta?.length > 0 ? (
                      liveData.buyerClosingCostsMeta?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.label || "—"}</td>
                          <td>
                            {getBuyerClosingCostUnit(item)}{" "}
                            {formatCurrency(getBuyerClosingCostValue(item))}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2">No Fixed Costs Available</td>
                      </tr>
                    )}

                    {templateformData?.commission_buyer !== false && (
                      <>
                        <tr>
                          <td>
                            Seller's Broker Compensation (
                            {liveData?.compensationMeta?.sellerBrokerCompensation ||
                              "0.00"}
                            %)
                          </td>
                          <td>
                            ${" "}
                            {formatCurrency(
                              liveData.compensationMeta
                                ?.sellerBrokerCompensationAmount,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            Buyer's Broker Compensation (
                            {liveData?.compensationMeta?.buyerBrokerCompensation ||
                              "0.00"}
                            %)
                          </td>
                          <td>
                            ${" "}
                            {formatCurrency(
                              liveData.compensationMeta
                                ?.buyerBrokerCompensationAmount,
                            )}
                          </td>
                        </tr>
                      </>
                    )}

                    {liveData?.closingCostsMeta?.length > 0 ? (
                      liveData.closingCostsMeta?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.label || "—"}</td>
                          <td>
                            {item.unit || "$"}{" "}
                            {/* ✅ Use computed total when available, fallback to value */}
                            {formatCurrency(
                              item.total != null ? item.total : item.value || 0,
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2">No Fixed Costs Available</td>
                      </tr>
                    )}

                    <tr>
                      <td>
                        <b>Total Fixed Costs</b>
                      </td>
                      <td>
                        <b>
                          ${" "}
                          {formatCurrency(
                            liveData?.totalClosingMeta?.totalClosingCosts || 0,
                          )}
                        </b>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* next button */}
          <div className="model-footer">
            <div className="model-popup-button-fixed next-step-btn">
              <button
                className="submit-btn btn-bg inpt-btn"
                onClick={() => setStep(2)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        // ✅ Step 2 — Next Actions
        <div className="model-body">
          <div className="model-popup-style">
            <div className="requiredlist-md">
              <ul>
                <li className="icon-requiredlist reqlst-edit">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleClose();
                      setTimeout(() => {
                        onEditManual();
                      }, 100);
                    }}
                  >
                    Open and edit the sheet manually
                  </button>
                </li>

                <li className="icon-requiredlist reqlst-download">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownloadPrintPDF("download", buyerformData);
                    }}
                  >
                    Download this sheet
                  </button>
                </li>
                <li className="icon-requiredlist reqlst-print">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownloadPrintPDF("print", buyerformData);
                    }}
                  >
                    Print this sheet
                  </button>
                </li>
                <li className="icon-requiredlist reqlst-email">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      const user = JSON.parse(localStorage.getItem("user"));
                      // Not `buyerformData` (the frozen prop from whenever
                      // this modal was first opened) — same stale-closure
                      // risk as Download/Print, plus this ensures the
                      // origination-fee row is converted from percent to a
                      // dollar amount even if ManualSheetSection never
                      // mounted this session.
                      const updated = buildBuyerPayload({
                        buyerformData: getEffectiveFormData(),
                        user,
                      });
                      openModal({
                        title: "Email a Closing Cost Estimate",
                        size: "mediumsizewidth",
                        content: (
                          <EmailModal
                            onClose={cancelResultModal}
                            type="buyer"
                            showToaster={showToaster}
                            buyerFormData={updated}
                          />
                        ),
                      });
                    }}
                  >
                    Email this sheet
                  </button>
                </li>
                {!isGuestUser(user) && (
                  <li
                    className="icon-requiredlist reqlst-save"
                    onClick={handleSaveCalc}
                  >
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                      }}
                    >
                      Save sheet to be accessed later
                    </button>
                  </li>
                )}
                <li className="icon-requiredlist reqlst-cancel">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      cancelModal();
                    }}
                  >
                    Cancel
                  </button>
                </li>
                <li className="icon-requiredlist reqlst-exit">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleExit(e);
                    }}
                  >
                    Exit
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
